import {defineStore} from "pinia";
import {useI18n} from "vue-i18n";
import {UserService} from "../services/user.service";
import {OrganizationService} from "@/javascript/services/organization.service";

export const useUserStore = defineStore('user', {

  state: () => (
    {
      t: useI18n().t,
      userService: new UserService(),
      organizationService: new OrganizationService(),
      user: null,
      userId: "",
      true_user: null,
      organizations: [],
      donations: [],
      donationSnapshot: [],
      subscriptions: [],
      currentOrganization: null,
      userScopedToSubdomain: false,
      userIsOrganizationMember: false,
      paymentProcessors: [],
      editSettings: false,
      localUser: null,
      errors: {},
      newPassword: '',
      confirmPassword: '',
      triggerSignOut: false,
      userDonationsMeta: {
        page: 1,
        pages: 1
      },
      loadingUserDonations : true,
      userDonationsYears: [],
    }
  ),

  actions: {
    loadData(organizationId) {
      return this.getUser()
        .then(_ => this.getMemberOrgs(organizationId))
        .catch((e) => {
          console.error("Error loading data", e);
          return false;
        });
    },

    getUser() {
      return this.userService.show(this.userId)
        .then((response) => {
          this.user = response.record;
          this.true_user = response.true_user;
        });
    },

    async loadUserDonationData() {
      try{
        this.donations = [];
        this.userDonationsMeta = {
          page: 1,
          pages: 1
        };
        await this.getUserDonations();
        await this.getUserDonationYears();
      } catch (e) {
        console.error("Error loading user donation data", e);
      }
    },

    async getUserDonations() {
      this.userDonationsLoading = true;
      const params = new URLSearchParams();
      params.set('query[status_in][]', 'completed');
      params.append('query[status_in][]', 'processing');
      params.set('query[organization_id_eq]', this.currentOrganization.id);
      params.set('query[s]', 'payments_paid_at desc');
      params.set('page', this.userDonationsMeta.page);
      params.set('limit', 10);

      return this.userService.getUserDonations(this.userId, params).then((result) => {
        if (result && result.data) {
          this.donations = result.data;
          this.userDonationsMeta = result.meta;
          if(this.userDonationsMeta.page === 1){
            this.donationSnapshot = result.data.slice(0, 5);
          }
          return true;
        } else {
          return false;
        }
      }).finally(() => {
        this.userDonationsLoading = false;
      });
    },

    async getUserDonationYears() {
      const params = new URLSearchParams();
      params.set('organization_id', this.currentOrganization.id);

      return this.userService.getUserDonationYears(this.userId, params).then((result) => {
        if (result && result.years) {
          this.userDonationsYears = result.years;
          return true;
        } else {
          return false;
        }
      });
    },

    nextPage() {
      if (this.userDonationsLoading) return;
      this.userDonationsMeta.page++;
      this.getUserDonations();
    },
    prevPage() {
      if (this.userDonationsLoading) return;
      this.userDonationsMeta.page--;
      this.getUserDonations();
    },
    moveToPage(page) {
      if (this.userDonationsLoading) return;
      this.userDonationsMeta.page = page;
      this.getUserDonations();
    },

    getUserSubscriptions(orgId) {
      const params = new URLSearchParams();
      params.set('query[status_in][]', 'active');
      params.append('query[status_in][]', 'paused');
      params.append('query[status_in][]', 'trialing');
      params.append('query[status_in][]', 'cancelled');
      if (orgId) params.set('query[organization_id_eq]', orgId);

      return this.userService.getUserSubscriptions(this.userId, params).then((result) => {
        if (result && result.data) {
          this.subscriptions = result.data;
          return true;
        } else {
          return false;
        }
      });
    },

    resetDonationData(){
      this.donations = [];
      this.donationSnapshot = [];
      this.subscriptions = [];
      this.userDonationsYears = [];
    },

    // Getting user memberships, then orgs they have access to
    // Easier way to do this, but this will filter out admin access to ensure memberships exist
    //Added ability to select a default org for those who are members of more than one.
    //That will be the org that they see first after login (if not using subdomain)
    getMemberOrgs(organizationId) {
      return this.userService.getPermittedOrgs(this.user.id)
        .then((res) => {
          this.organizations = res.data?.organizations;
          this.userScopedToSubdomain = res.data?.scoped_subdomain; // Check if the app is scoped to a specific subdomain
          // Set the current organization
          if (this.organizations?.length > 0) {
            this.userIsOrganizationMember = true;
          }
          let currentOrg;

          // if org Id is passed from the url, try and find it
          if (organizationId) {
            currentOrg = this.organizations.find(o => o.slug === organizationId);
          }
          if (currentOrg) {
            this.currentOrganization = currentOrg;
          } else if (this.user.default_organization_id) {
            // User has a saved default org preference
            currentOrg = this.organizations.find(o => o.id === this.user.default_organization_id);
            this.currentOrganization = currentOrg || this.organizations.find(o => !o.parent_id) || this.organizations[0];
          } else if (this.organizations?.length > 0) {
            // Default to parent org (no parent_id), else first in list
            this.currentOrganization = this.organizations.find(o => !o.parent_id) || this.organizations[0];
          }
          return true;
        });
    },

    async getDonorOrgs(organizationId) {
      return this.userService.getDonorOrgs(this.user.id)
        .then((res) => {
          this.organizations = res.data;
          // Set the current organization
          let currentOrg;

          // if org Id is passed from the url, try and find it
          if (organizationId) {
            currentOrg = this.organizations.find(o => o.slug === organizationId);
          }
          if (currentOrg) {
            this.currentOrganization = currentOrg;
          } else if (this.organizations?.length > 0) {
            // If not, default to the first organization the user has access to
            this.currentOrganization = this.organizations[0];
          }
          return true;
        });
    },

    async getDonorProfile(organizationId) {
      const params = new URLSearchParams();
      params.set('query[organization_id_eq]', organizationId);
      const donorProfiles = await this.userService.getDonorProfile(this.user.id, params);
      return donorProfiles?.data[0];
    },

    acceptTermsOfService() {
      return this.userService.update(
        this.user.id,
        {
          ...this.user,
          terms_of_service_agreed: true,
        }
      );
    },

    signOut() {
      return this.userService.signOut().then(() => {
        //Clear Data
        this.user = null;
        this.userId = "";
        this.organizations = [];
        this.currentOrganization = null;

        window.location.reload();
        return true;
      });
    },
    //Connects with sign in method from User Service
    //This is like the middle man - SignInModal uses this
    signIn(credentials) {
      this.user = null;
      this.userId = "";
      this.organizations = [];
      this.currentOrganization = null;

      return this.userService.signIn(credentials).then((response) => {
        if (response.ok) {
          return response.json().then((data) => {
            // console.log('Response data:', data);
            if (data && data.user) {
              this.user = data.user;
              this.userId = data.user.id;

              // Update CSRF token if provided
              if (data.csrf_token) {
                const csrfMeta = document.querySelector('meta[name="csrf-token"]');
                if (csrfMeta) {
                  csrfMeta.content = data.csrf_token;
                }
              }

              // If we're using org scoped subdomains, we need to reload the page to pick up the new org
              if (this.userScopedToSubdomain) {
                window.location.reload();
              }
                return data.user;
            }
            console.error('No user in response data');
            return false;
          });
        } else {
          console.error('Response not OK:', response.status);
          return false;
        }
      }).catch((error) => {
        console.error('Sign in catch error:', error);
        return false;
      });
    },

    async rememberMe() {
      const result_1 = await this.userService.rememberMe();
      if (result_1) {
        this.loadData();
        return true;
      } else {
        return false;
      }
    },

    goToAdmin(newWindow = true) {
      const finalUrl = "/admin/users";
      if(newWindow) {
        window.open(finalUrl, '_blank');
      } else {
        window.location.href = finalUrl;
      }
    },

    toggleEditSettings() {
      this.editSettings = !this.editSettings;
      if (this.editSettings) {
        this.localUser = {...this.user};  // Create a copy of user data
      } else {
        this.localUser = null;  // Discard the copy if editing is canceled
        this.errors = {};
      }
    },

    saveSettings() {
      if (this.validateForm()) {
        if (this.localUser) {
          return this.userService.update(this.user.id, this.localUser).then(_ => {
            this.user = {...this.localUser};  // Update original user data
            this.toggleEditSettings();
            this.errors = {};
          });
        }
      }
    },

    save() {
      return this.userService.update(this.user.id, this.user);
      },

    setDefaultOrganization(organizationId) {
      return this.userService.update(this.user.id, { user: { default_organization_id: organizationId } }).then(() => {
        this.user.default_organization_id = organizationId;
      }).catch((e) => {
        console.error("Failed to set default organization", e);
        throw e;
      });
    },

    cancelEditSettings() {
      this.toggleEditSettings();  // Just toggle off edit mode and discard changes
    },

    validateForm() {
      let valid = true;
      this.errors = {}; // Reset errors

      if (!this.localUser.first_name || this.localUser.first_name.length < 3) {
        this.errors['first_name'] = "First name can't be empty and must be at least 3 characters";
        valid = false;
      }

      if (!this.localUser.last_name || this.localUser.last_name.length < 3) {
        this.errors['last_name'] = "Last name can't be empty and must be at least 3 characters";
        valid = false;
      }

      return valid;
    },

    changePassword() {
      if (this.validatePasswordForm()) {
        this.userService.changePassword(this.user.id, this.newPassword)
          .then(() => {
            this.triggerSignOut = true;
            this.newPassword = '';
            this.confirmPassword = '';
          })
          .catch((error) => {
            console.error('Failed to change password', error);
            return false;
          });
      }
    },

    validatePasswordForm() {
      let valid = true;
      this.errors = {}; // Reset errors

      if (!this.newPassword || this.newPassword.length < 6) {
        this.errors['new_password'] = "New password must be at least 6 characters";
        valid = false;
      }

      if (this.newPassword !== this.confirmPassword) {
        this.errors['confirm_password'] = "Passwords do not match";
        valid = false;
      }

      return valid;
    },

    stopImpersonation() {
      return this.userService.stopImpersonation().then(() => {
        window.location.reload();
      });
    }
  },
  getters: {
    userIsAdmin(state) {
      return state.user && state.user.role === "Admin";
    },

    userAgreedToAllTerms(state) {
      return state.user.terms_of_service_agreed;
    },

    isBeingImpersonated(state) {
      return state.true_user && (state.true_user.id !== state.user.id);
    }
  }
});
